<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'
import PageContainer from '@/components/common/PageContainer.vue'
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import NoSubscriptionBanner from '@/features/billing/components/NoSubscriptionBanner.vue'
import OrganizationOnboardingSteps from '@/features/organizations/components/OrganizationOnboardingSteps.vue'
import { useCheckoutCompletionSync } from '@/features/billing/composables/useCheckoutCompletionSync'
import { useSyncMyOrganizations } from '@/features/organizations/composables/useSyncMyOrganizations'
import { can } from '@/lib/permissions/can'
import type { PermissionKey } from '@/lib/permissions/keys'
import { useUiStore } from '@/stores/ui'
import { useWorkspaceStore } from '@/stores/workspace'

const ui = useUiStore()
useSyncMyOrganizations()
useCheckoutCompletionSync()

const route = useRoute()
const router = useRouter()
const workspace = useWorkspaceStore()

// Switching organization/workspace can revoke access to the page being viewed.
watch(
  () => [workspace.currentOrganizationId, workspace.currentAccountId],
  () => {
    const permission = route.meta.permission as PermissionKey | undefined

    if (permission && !can(permission)) router.replace('/dashboard')
  },
)

// Small screens start with the sidebar collapsed to icons. Expanding it there is temporary,
// so it doesn't overwrite the stored preference used on wider screens.
const isSmallScreen = useMediaQuery('(max-width: 700px)')
const expandedOnSmallScreen = ref(false)

watch(isSmallScreen, () => {
  expandedOnSmallScreen.value = false
})

// SidebarProvider's `open` means expanded — inverse of our stored "collapsed" preference.
const sidebarOpen = computed({
  get: () => (isSmallScreen.value ? expandedOnSmallScreen.value : !ui.sidebarCollapsed),
  set: (open: boolean) => {
    if (isSmallScreen.value) expandedOnSmallScreen.value = open
    else ui.sidebarCollapsed = !open
  },
})
</script>

<template>
  <div class="flex min-h-svh flex-col" style="--header-height: 3.75rem">
    <AppHeader />
    <SidebarProvider
      v-model:open="sidebarOpen"
      class="min-h-[calc(100svh-var(--header-height))]"
      style="--sidebar-width: 15rem"
    >
      <AppSidebar />
      <SidebarInset>
        <main class="flex-1 overflow-y-auto p-6">
          <PageContainer>
            <NoSubscriptionBanner />
            <OrganizationOnboardingSteps />
          </PageContainer>
          <RouterView />
        </main>
      </SidebarInset>
    </SidebarProvider>
  </div>
</template>
